import type {
  AICaptionMediaItem,
  ApprovalRequest,
  ApprovalRequestStatusUpdate,
  BlueskyScheduledPost,
  BulkScheduledPostRequest,
  BulkScheduledPostResponse,
  Event,
  FacebookScheduledPost,
  GenerateAICaptionsResponse,
  GetLinkedinFollowersSearchParams,
  GetLinkedinMemberDetailsParams,
  InstagramScheduledPost,
  LinkedInScheduledPost,
  ListRSSFeedsResponse,
  MemberProfilesResponse,
  PinterestScheduledPost,
  PostReorderRequest,
  RecommendedPublishTimesResponse,
  ReorderResponse,
  RSSFeedRequest,
  RSSFeedResponse,
  RSSFeedValidationRequest,
  RSSFeedValidationResponse,
  SnapchatScheduledPost,
  ThreadsScheduledPost,
  TikTokScheduledPost,
  TwitterScheduledPost,
  UpdateFacebookRequest,
  UpdateInstagramRequest,
  UpdateLinkedInRequest,
  UpdatePinterestRequest,
  UpdateRSSFeedRequest,
  UpdateSnapchatRequest,
  UpdateTikTokRequest,
  UpdateYouTubeRequest,
  YouTubeScheduledPost,
} from '@dashhudson/api-client/scheduler/model';

import merge from 'lodash/merge';

import type { APPROVAL_STATUS } from '@/app/scheduler/constants';
import type { CHANNEL_FILTER_OPTIONS } from '@/app/scheduler/constants/schedulerFilters';
import type { HttpRequestConfig } from '@/types/http';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';
import { parseQuery } from '@/utils/query';

export const http = createDashHttpClient(env.schedulerApiUrl, {
  camelizeKeys: true,
  decamelizeIgnoreKeys: ['pinterestBoard'],
});

// `platform` is the URL segment (`facebook`, `instagram`, `tiktok`, …) chosen
// at call sites; we keep it as `string` to avoid coupling the API surface to
// the app-level Platform enum.
type Platform = string;

// Union of every per-platform scheduled-post shape returned by the per-platform
// scheduled_posts endpoints. The caller picks the runtime platform, so the
// concrete type can only be narrowed downstream. Named `PlatformScheduledPost`
// to avoid clashing with the app-domain `ScheduledPost` in
// `@/app/scheduler/types/post.ts` — that one is a loose hand-rolled shape; this
// is the spec-generated wire union.
export type PlatformScheduledPost =
  | BlueskyScheduledPost
  | FacebookScheduledPost
  | InstagramScheduledPost
  | LinkedInScheduledPost
  | PinterestScheduledPost
  | SnapchatScheduledPost
  | ThreadsScheduledPost
  | TikTokScheduledPost
  | TwitterScheduledPost
  | YouTubeScheduledPost;

// Body accepted by POST /{platform}/scheduled_posts and its /batch sibling —
// the spec models the create payload as the full ScheduledPost shape per
// platform.
export type CreatePostRequest = PlatformScheduledPost;

// Body accepted by PATCH /{platform}/scheduled_posts/{id}. Most platforms have
// a dedicated Update*Request shape; Bluesky/Threads/Twitter share the
// ScheduledPost shape on patch (no Update*Request type generated).
export type UpdatePostRequest =
  | UpdateFacebookRequest
  | UpdateInstagramRequest
  | UpdateLinkedInRequest
  | UpdatePinterestRequest
  | UpdateSnapchatRequest
  | UpdateTikTokRequest
  | UpdateYouTubeRequest
  | BlueskyScheduledPost
  | ThreadsScheduledPost
  | TwitterScheduledPost;

export interface FetchPostsParams {
  platform: Platform;
  brandId?: number;
  brandIds?: number[] | string;
  start?: string;
  end?: string;
  sort?: string;
  status?: string[];
  limit?: number;
  hasScheduledTime?: boolean;
}

// Response is the per-platform List*PostsResponse shape (only a subset of
// platforms have a typed list response in the spec), so we leave the response
// loose and let callers narrow.
export const fetchPosts = (
  {
    platform,
    brandId,
    brandIds,
    start,
    end,
    sort,
    status,
    limit,
    hasScheduledTime,
  }: FetchPostsParams,
  config: HttpRequestConfig = {},
) =>
  http.get(`/${platform}/scheduled_posts`, {
    ...config,
    params: {
      brandId,
      brandIds,
      start,
      end,
      sort,
      status: status?.join?.(','),
      limit,
      hasScheduledTime,
      mediaV2: true,
    },
  });

export const fetchPost = (
  { platform, id }: { platform: Platform; id: number },
  config: HttpRequestConfig = {},
) =>
  http.get<PlatformScheduledPost>(`/${platform}/scheduled_posts/${id}`, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const createPost = (
  { platform, data }: { platform: Platform; data: CreatePostRequest },
  config: HttpRequestConfig = {},
) =>
  http.post(`/${platform}/scheduled_posts`, data, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const batchCreatePost = (
  { platform, data }: { platform: Platform; data: CreatePostRequest },
  config: HttpRequestConfig = {},
) =>
  http.post<PlatformScheduledPost[]>(`/${platform}/scheduled_posts/batch`, data, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const fetchPostGroup = (
  { postGroupId, brandId }: { postGroupId: string; brandId: number },
  config: HttpRequestConfig = {},
) =>
  http.get<{ data: PlatformScheduledPost[] }>(`/scheduled_posts/groups/${postGroupId}`, {
    ...config,
    params: {
      brandId,
      resolveReferences: true,
      mediaV2: true,
    },
  });

export const createBulkPosts = (
  {
    posts,
    bulkSessionId,
    brandId,
  }: { posts: BulkScheduledPostRequest['posts']; bulkSessionId: string; brandId: number },
  config: HttpRequestConfig = {},
) =>
  http.post<BulkScheduledPostResponse>(
    '/scheduled_posts/bulk',
    { posts, bulkSessionId, brandId },
    config,
  );

export const updatePost = (
  { platform, id, data }: { platform: Platform; id: number; data: UpdatePostRequest },
  config: HttpRequestConfig = {},
) =>
  http.patch(`/${platform}/scheduled_posts/${id}`, data, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const deletePost = (
  { platform, id }: { platform: Platform; id: number },
  config: HttpRequestConfig = {},
) =>
  http.delete<void>(`/${platform}/scheduled_posts/${id}`, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const reorderPost = (
  { platform, id, brandId, after }: { platform: Platform; id: number } & PostReorderRequest,
  config: HttpRequestConfig = {},
) =>
  http.post<ReorderResponse>(
    `/${platform}/scheduled_posts/${id}/reorder`,
    { after, brandId },
    {
      ...config,
      params: {
        mediaV2: true,
      },
    },
  );

export const fetchEvents = ({ brandId }: { brandId: number }, config: HttpRequestConfig = {}) =>
  http.get<Event[]>('/events', {
    ...config,
    params: { brandId },
  });

export const createEvent = (
  { brandId, title, startDate, endDate, description, color }: Event,
  config: HttpRequestConfig = {},
) =>
  http.post<Event>(
    '/events',
    {
      brandId,
      title,
      startDate,
      endDate,
      description,
      color,
    },
    config,
  );

export const updateEvent = (
  { id, brandId, title, startDate, endDate, description, color }: Event,
  config: HttpRequestConfig = {},
) =>
  http.patch<Event>(
    `/events/${id}`,
    {
      brandId,
      title,
      startDate,
      endDate,
      description,
      color,
    },
    config,
  );

export const deleteEvent = ({ id }: { id: number }, config: HttpRequestConfig = {}) =>
  http.delete<void>(`/events/${id}`, config);

export const updateApprovalRequest = (
  {
    approvalRequestId,
    approvalStatus,
  }: { approvalRequestId: number; approvalStatus: ApprovalRequestStatusUpdate['approvalStatus'] },
  config: HttpRequestConfig = {},
) =>
  http.patch<ApprovalRequest>(
    `/approval_requests/${approvalRequestId}`,
    { approval_status: approvalStatus },
    config,
  );

export async function getRecommendedPublishTimes(
  { brandId, platform, timezone }: { brandId: number; platform: string; timezone: string },
  config: HttpRequestConfig = {},
) {
  return http.get<RecommendedPublishTimesResponse>(`/recommended_publish_times`, {
    ...config,
    params: { brandId, platform_type: platform.toUpperCase(), timezone },
  });
}

/**
 * Validates an RSS feed URL with brand and name context.
 */
export async function validateRssFeed(
  { url, brandId, name, rssFeedId }: RSSFeedValidationRequest,
  config: HttpRequestConfig = {},
) {
  const data: RSSFeedValidationRequest = { url, brandId, name };
  if (rssFeedId) {
    data.rssFeedId = rssFeedId;
  }
  return http.post<RSSFeedValidationResponse>(`/rss_feeds/validate`, data, config);
}

/**
 * Retrieves a list of RSS feeds for specified brand IDs. `url`, when provided,
 * is parsed for additional query params (used to follow pagination cursors).
 */
export async function getRssFeedsList(
  {
    brandIds,
    url,
    limit,
    orderByBrandIds,
  }: {
    brandIds: number[] | number | string;
    url?: string;
    limit?: number;
    orderByBrandIds?: boolean;
  },
  config: HttpRequestConfig = {},
) {
  const params: Record<string, unknown> = {};
  if (url) {
    merge(params, parseQuery(url));
  }
  return http.get<ListRSSFeedsResponse>(`/rss_feeds`, {
    ...config,
    params: {
      ...params,
      brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds,
      limit,
      orderByBrandIds,
    },
  });
}

/**
 * Retrieves a single RSS feed by ID.
 */
export async function getRssFeed(id: number, config: HttpRequestConfig = {}) {
  return http.get<RSSFeedResponse>(`/rss_feeds/${id}`, config);
}

/**
 * Creates a new RSS feed with channel configurations.
 */
export async function createRssFeed(
  { brandId, url, name, frequency, status, enabled, channelConfigs, sampleItem }: RSSFeedRequest,
  config: HttpRequestConfig = {},
) {
  const data: RSSFeedRequest = {
    brandId,
    url,
    name,
    frequency,
    status,
    enabled,
    channelConfigs,
    sampleItem,
  };

  return http.post<RSSFeedResponse>(`/rss_feeds`, data, config);
}

/**
 * Updates an existing RSS feed with new configurations. `brandId` is not in
 * the spec's UpdateRSSFeedRequest but our callers send it; the backend
 * tolerates it so we preserve the existing wire payload.
 */
export async function updateRssFeed(
  {
    id,
    brandId,
    url,
    name,
    frequency,
    status,
    enabled,
    channelConfigs,
    sampleItem,
  }: UpdateRSSFeedRequest & { id: number; brandId?: number },
  config: HttpRequestConfig = {},
) {
  const data: UpdateRSSFeedRequest & { brandId?: number } = {
    brandId,
    url,
    name,
    frequency,
    status,
    enabled,
    channelConfigs,
    sampleItem,
  };

  return http.patch<RSSFeedResponse>(`/rss_feeds/${id}`, data, config);
}

/**
 * Deletes an RSS feed by ID.
 */
export async function deleteRssFeed(id: number, config: HttpRequestConfig = {}) {
  return http.delete<void>(`/rss_feeds/${id}`, config);
}

/**
 * Search for LinkedIn organization followers for @mentions.
 */
export async function searchLinkedInFollowers(
  { brandId, keyword }: GetLinkedinFollowersSearchParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/linkedin/followers/search`, {
    params: { brand_id: brandId, keyword },
    ...config,
  });
}

/**
 * Get LinkedIn member details (avatar, name, headline) by URNs.
 */
export async function getLinkedInMemberDetails(
  { brandId, personUrns }: GetLinkedinMemberDetailsParams,
  config: HttpRequestConfig = {},
) {
  return http.get<MemberProfilesResponse>(`/linkedin/member_details`, {
    params: { brand_id: brandId, person_urns: personUrns },
    paramsSerializer: {
      indexes: null, // serializes as person_urns=x&person_urns=y (no brackets)
    },
    ...config,
  });
}

/**
 * Generate AI captions for bulk media items. `platform` and `mediaType` are
 * loosened to `string` so callers can forward the runtime platform/media
 * strings they hold — the spec types them as enums but the wire contract
 * accepts the raw string values.
 */
export interface GenerateAICaptionsParams {
  brandId: number;
  platform: string;
  mediaItems: Array<Omit<AICaptionMediaItem, 'mediaType'> & { mediaType: string }>;
}

export async function generateAICaptions(
  { brandId, platform, mediaItems }: GenerateAICaptionsParams,
  config: HttpRequestConfig = {},
) {
  return http.post<GenerateAICaptionsResponse>(
    '/ai_captions',
    { brandId, platform, mediaItems },
    { timeout: 30000, ...config },
  );
}

// ---------------------------------------------------------------------------
// Shareable calendar links. The types/interfaces below are the API contract and
// stay when the backend lands; the mock data + methods further down are temporary
// — see the "MOCK" block and TODO(sc-201952) markers.
// ---------------------------------------------------------------------------

export type ShareableCalendarLinkChannel = keyof typeof CHANNEL_FILTER_OPTIONS;

export type ShareableCalendarLinkApprovalStatus = keyof typeof APPROVAL_STATUS;

export interface ShareableCalendarLinkFilters {
  brandIds: number[];
  channels: ShareableCalendarLinkChannel[];
  approvalStatuses: ShareableCalendarLinkApprovalStatus[];
  showDrafts: boolean;
}

export interface ShareableCalendarLink {
  id: number;
  name: string;
  enabled: boolean;
  validFrom: string;
  validUntil: string;
  shareToken: string;
  url: string;
  filters: ShareableCalendarLinkFilters;
}

export interface ShareableCalendarLinkListResponse {
  data: ShareableCalendarLink[];
}

export interface CreateShareableCalendarLinkRequest {
  name: string;
  validUntil: string;
  filters: ShareableCalendarLinkFilters;
}

export interface UpdateShareableCalendarLinkRequest {
  name: string;
  enabled: boolean;
  validUntil: string;
  filters: ShareableCalendarLinkFilters;
}

// ===== MOCK START — remove this whole block when sc-201952 (scheduler-backend) lands =======
const mockDaysFromNow = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
};
// One link per display status (Active / Expiring Soon / Expired / Disabled) for previewing the UI.
let mockShareableCalendarLinks: ShareableCalendarLink[] = [
  {
    id: 1,
    name: 'Active Content Calendar',
    enabled: true,
    validFrom: mockDaysFromNow(-30),
    validUntil: mockDaysFromNow(60),
    shareToken: 'share-mock0001',
    url: 'https://app.dashsocial.com/scheduler/shared/1?dash_token=share-mock0001',
    filters: {
      brandIds: [144, 1, 3550, 1999],
      channels: ['INSTAGRAM', 'TIKTOK', 'FACEBOOK'],
      approvalStatuses: ['APPROVED', 'PENDING'],
      showDrafts: true,
    },
  },
  {
    id: 2,
    name: 'Expiring Soon Content Calendar',
    enabled: true,
    validFrom: mockDaysFromNow(-25),
    validUntil: mockDaysFromNow(3),
    shareToken: 'share-mock0002',
    url: 'https://app.dashsocial.com/scheduler/shared/2?dash_token=share-mock0002',
    filters: {
      brandIds: [144, 3550],
      channels: ['INSTAGRAM_STORY', 'PINTEREST'],
      approvalStatuses: ['PENDING'],
      showDrafts: false,
    },
  },
  {
    id: 3,
    name: 'Expired Content Calendar',
    enabled: true,
    validFrom: mockDaysFromNow(-60),
    validUntil: mockDaysFromNow(-5),
    shareToken: 'share-mock0003',
    url: 'https://app.dashsocial.com/scheduler/shared/3?dash_token=share-mock0003',
    filters: {
      brandIds: [1],
      channels: ['LINKEDIN'],
      approvalStatuses: ['APPROVED'],
      showDrafts: true,
    },
  },
  {
    id: 4,
    name: 'Disabled Content Calendar',
    enabled: false,
    validFrom: mockDaysFromNow(-10),
    validUntil: mockDaysFromNow(30),
    shareToken: 'share-mock0004',
    url: 'https://app.dashsocial.com/scheduler/shared/4?dash_token=share-mock0004',
    filters: {
      brandIds: [1999],
      channels: ['YOUTUBE', 'THREADS'],
      approvalStatuses: ['APPROVED'],
      showDrafts: false,
    },
  },
];
let mockNextId = 5;

export const getShareableCalendarLinks = (config: HttpRequestConfig = {}) => {
  // TODO(sc-201952): return http.get<ShareableCalendarLinkListResponse>('/shareable_calendar_links', { ...config });
  void config;
  return Promise.resolve({ data: { data: [...mockShareableCalendarLinks] } });
};

export const createShareableCalendarLink = (
  data: CreateShareableCalendarLinkRequest,
  config: HttpRequestConfig = {},
) => {
  // TODO(sc-201952): return http.post<ShareableCalendarLink>('/shareable_calendar_links', data, { ...config });
  void config;
  const id = mockNextId++;
  const token = `share-mock${String(id).padStart(4, '0')}`;
  const link: ShareableCalendarLink = {
    id,
    name: data.name,
    enabled: true,
    validFrom: new Date().toISOString(),
    validUntil: data.validUntil,
    shareToken: token,
    url: `https://app.dashsocial.com/scheduler/shared/${id}?dash_token=${token}`,
    filters: data.filters,
  };
  mockShareableCalendarLinks = [link, ...mockShareableCalendarLinks];
  return Promise.resolve({ data: link });
};

export const updateShareableCalendarLink = (
  { shareableLinkId, ...data }: { shareableLinkId: number } & UpdateShareableCalendarLinkRequest,
  config: HttpRequestConfig = {},
) => {
  // TODO(sc-201952): return http.patch<ShareableCalendarLink>(`/shareable_calendar_links/${shareableLinkId}`, data, { ...config });
  void config;
  mockShareableCalendarLinks = mockShareableCalendarLinks.map((link) =>
    link.id === shareableLinkId ? { ...link, ...data } : link,
  );
  const updated = mockShareableCalendarLinks.find((link) => link.id === shareableLinkId);
  return Promise.resolve({ data: updated });
};

export const deleteShareableCalendarLink = (
  { shareableLinkId }: { shareableLinkId: number },
  config: HttpRequestConfig = {},
) => {
  // TODO(sc-201952): return http.delete<void>(`/shareable_calendar_links/${shareableLinkId}`, { ...config });
  void config;
  mockShareableCalendarLinks = mockShareableCalendarLinks.filter(
    (link) => link.id !== shareableLinkId,
  );
  return Promise.resolve({ data: undefined });
};
// ===== MOCK END ============================================================================
